import { defineComponent, ref, onMounted, computed } from 'vue'
import UIKit from 'uikit'

import PointPage from '@/organizations/_default/point'
import Banner from '@/organizations/cosmo/default/components/Banner.vue'
import CosmoHeader from '@/organizations/cosmo/default/components/CosmoHeader.vue'
import CosmoFooter from '@/organizations/cosmo/default/components/CosmoFooter.vue'
import BusinessHours from '@/organizations/cosmo/default/components/BusinessHours.vue'
import MaintenanceLink from '@/organizations/cosmo/default/components/MaintenanceLink.vue'
import PointSearchConditions from '@/components/common/PointSearchConditions.vue'
import ProductModal from '@/components/common/ProductModal.vue'

import Page from "@/services/Page.js"
import { Filters } from '@/services/Filters.ts'
import functions from './utils/functions.ts'

export default defineComponent({
  extends: PointPage,
  components: {
    Banner,
    CosmoHeader,
    CosmoFooter,
    BusinessHours,
    PointSearchConditions,
    ProductModal,
    MaintenanceLink,
  },
  setup(props, context) {
    const page = PointPage.setup(props, context)

    // アプリ用に引き継ぐクエリを追加指定
    const queries = ['btn', 'sh', 'once', 'frm', 'skip', 'view']
    queries.forEach((query) => {
      Page.forwardQueries[query] = {}
    })

    /**
     * ページがiframeで表示されているかどうか
     * NOTE: iframeで表示されている場合は外部リンクを非表示にする
     * @type {boolean}
     */
    const isIframe = Object.keys(storelocator.url_params).includes('iframe')

    /**
     * カーメンテナンス予約リンクのラベル
     */
    const maintenanceLinkLabel = 'ネットで来店予約'
    /**
     * カーメンテナンス予約リンクのフォーマット
     * {id} にストアIDが入る
     */
    const maintenanceLinkFormat = 'https://timy.jp/{id}?reserve_channel=4&utm_campaign=alliance&utm_medium=Affiliates&&utm_source=ceh_map'
    /**
     * ストアIDが格納されている追加フィールドのキー
     */
    const maintenanceLinkExtraFieldKey = 'Timyリンク用ストアID'

    /**
     * 検索条件のプレビュー表示件数 この数値より上であれば格納されてボタンが表示される
     */
    const previewConditionsCount = 4

    // 選択した条件とその他を分割するために利用
    const searchConditionsState = ref({
      isSelected: false,
      selectedIds: [],
      notSelectedIds: []
    })

    /**
     * UIKitのnavコンポーネントインスタンス
     * サービスリストの折りたたみ/展開を制御するために使用
     * onMountedでDOM要素（.service-list-summary）に紐づけられる
     * @type {object|null}
     */
    let serviceListNav = null

    /**
     * サービスリストの折りたたみ/展開状態
     * @type {object}
     */
    const isServiceListOpen = ref(false)

    if (sessionStorage) {
      const cache = Filters.cache.load()
      const filterIds = cache?.search_conditions || []

      // 選択した条件とその他を分割
      if (filterIds && filterIds.length > 0) {
        const ids = Filters.getPointFilters(
          Filters.TYPE_SEARCH_CONDITIONS,
          page.point.value,
          Filters.getFilterTree(Filters.TYPE_SEARCH_CONDITIONS)
        ).filter(c => c.is_show).map(c => c.id)

        if (ids.length > 0) {
          searchConditionsState.value.isSelected = true
          ids.forEach((id) => {
            if (filterIds.includes(id)) {
              searchConditionsState.value.selectedIds.push(id)
            } else {
              searchConditionsState.value.notSelectedIds.push(id)
            }
          })
        }
      }
    }

    /**
     * 検索条件の件数を取得
     * @returns {number} 検索条件の数
     */
    const getConditionsCount = () => {
      const uniqueNames = new Set()
      return Filters
        .getPointFilters(
          Filters.TYPE_SEARCH_CONDITIONS,
          page.point.value,
          Filters.getFilterTree(Filters.TYPE_SEARCH_CONDITIONS)
        )
        .filter(condition => condition.is_show)
        .reduce((count, condition) => {
          if (!uniqueNames.has(condition.name)) {
            uniqueNames.add(condition.name)
            return count + 1
          }
          return count
        }, 0)
    }

    /**
     * 店舗の持つ有効な検索条件の件数
     * @type {number}
     */
    const conditionsCount = getConditionsCount()

    /**
     * コンポーネントの名前に合わせて変数名をproductModalRefとするが
     * モーダルに表示する内容は絞り込みの説明
     */
    const productModalRef = ref(null)
    const selectedSearchCondition = ref({})

    /**
     * FileterInfoのクリックイベント
     * 共通のproductModalを利用するために、propsで設定する
     */
    const onClickedSearchCondition = (searchCondition) => {
      selectedSearchCondition.value = searchCondition
      productModalRef.value.open()
    }

    /**
     * 検索上件のアイコンを表示するか
     * true: 表示する
     */
    const searchConditionIsOpenable = (serachCondition) => {
      return serachCondition.extra_fields?.description
    }


    /**
     * サービスリストの開閉をトグル
     * @param {Object} searchCondition
     */
    const toggleServiceList = async (event) => {
      if (event) {
        // クリックイベントをキャンセル
        event.preventDefault()
        event.stopPropagation()
      }
      
      if (serviceListNav) {
        // UIKitのnavコンポーネントを利用してサービスリストをトグル
        serviceListNav.toggle()
      }
      isServiceListOpen.value = !isServiceListOpen.value
    }

    onMounted(() => {
      const element = document.querySelector('.service-list-summary')
      serviceListNav = UIKit.nav(element)

      if(storelocator.url_params.iframe !== undefined) {
        // アプリ用のトラッキングイベントを送信
        functions.sendScreenEvent('SSSENTAKU_DETAIL')

        // viewパラメータのついている場合はアプリ向けのボタンを表示させないためのクラスをbodyに追加
        if(storelocator.url_params.view !== undefined) {
          document.querySelector('body')?.classList.add('view-only-mode')
        }

        // 「電話する」ボタンのトラッキングイベントを設定
        const telElement = document.querySelector('.caller-wrapper .caller .tel')
        if (telElement) {
          telElement.addEventListener('click', () => {
            functions.handlePhoneCall(page.point.value)
          })
        }
      }
    })

    /**
     * ポイントのストアIDからカーメンテナンス予約リンクを生成
     * @param {Object} point
     * @returns {string} url
     */
    const genarateMaintenanceLink = (point) => {
      const storeId = point.extra_fields?.[maintenanceLinkExtraFieldKey]
      // iframeで表示されている場合はリンクを生成しない
      if (!storeId || isIframe) {
        return ''
      }

      return maintenanceLinkFormat.replace(/\{id\}/g, storeId)
    }
    
    /**
     * セルフサービスステーションを恒常的に選択するハンドラー
     * @returns {void}
     */
    const onSelectSS = () => {
      functions.handleSelectedSS(page.point.value)
    }
    /**
     * セルフサービスステーションを一時的に選択するハンドラー
     * @returns {void}
     */
    const onOnceSelectSS = () => {
      functions.handleOnceSelectedSS(page.point.value)
    }

    /**
     * アプリ用のボタンを表示するか
     * @returns {boolean} true: 表示する
     */
    const shouldShowAppButton = computed(() => {
      // iframe以外では表示しない
      if (!isIframe) {
        return false
      }
      
      const extraFields = page.point.value?.extra_fields
      if (!extraFields) {
        return false
      }
      
      // sh=1の場合はコミっと車検フラグをチェック
      if (storelocator.url_params.sh == '1') {
        return extraFields['コミっと車検'] == '1'
      }
      
      // sh=1以外の場合はコスモアプリフラグをチェック
      return extraFields['コスモの公式アプリ'] == '1'
    })


    return {
      ...page,  
      Banner,
      BusinessHours,
      MaintenanceLink,
      maintenanceLinkLabel,
      selectedSearchCondition,
      productModalRef,
      searchConditionsState,
      onClickedSearchCondition,
      searchConditionIsOpenable,
      genarateMaintenanceLink,
      toggleServiceList,
      isServiceListOpen,
      conditionsCount,
      previewConditionsCount,
      onSelectSS,
      onOnceSelectSS,
      shouldShowAppButton,
      isIframe
    }
  }
})
